import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import CheckIcon from '@material-ui/icons/Check';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import { greens, golds, greys, reds, onwardNavy } from '@/styles/colors';
import {
    orderShipperTotal,
    orderCarrierTotal,
    orderMarginDollar,
    orderMarginPct,
    routeMarginDollar,
    routeMarginPct,
    marginHealth,
    canViewShipperAggregate,
} from './utils';

const HEALTH_COLORS = { good: greens[0], thin: golds[0], bad: reds.cancelled, neutral: greys[1] };
const HEALTH_BG = { good: '#eaf6ec', thin: '#fbf1e0', bad: '#fbeaec', neutral: '#f0f0f0' };

const dollarFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const Root = styled.div`
    padding: 26px 30px 32px;
    display: flex;
    flex-direction: column;
    gap: 22px;
`;

const Heading = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Title = styled.span`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 800;
    font-size: 24px;
    color: ${onwardNavy};
    letter-spacing: -0.01em;
`;

const CountChip = styled.span`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 12px;
    color: ${greys[2]};
    background: ${greys[5]};
    padding: 4px 10px;
    border-radius: 20px;
`;

const HeadlineCard = styled.div`
    border: 1px solid ${greys[4]};
    border-radius: 10px;
    overflow: hidden;
    background: white;
    display: flex;
`;

const StatCell = styled.div`
    flex: 1;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Divider = styled.div`
    width: 1px;
    background: ${greys[4]};
    margin: 14px 0;
`;

const StatLabel = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 11.5px;
    color: ${greys[1]};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const StatValue = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 800;
    font-size: 22px;
    color: ${onwardNavy};
    letter-spacing: -0.01em;
`;

const StatSub = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 600;
    font-size: 12.5px;
    color: ${greys[1]};
`;

const TableWrap = styled.div`
    border: 1px solid ${greys[4]};
    border-radius: 10px;
    overflow: hidden;
    background: white;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const thBase = css`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-size: 11px;
    font-weight: 700;
    color: ${greys[1]};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 13px 16px 11px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
`;
const TH = ({ right, children, ...rest }) => (
    <th
        css={css`
            ${thBase};
            ${right ? 'text-align: right;' : ''}
        `}
        {...rest}
    >
        {children}
    </th>
);

const tdBase = css`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-size: 14px;
    color: ${greys[3]};
    padding: 12px 16px;
    border-top: 1px solid #f0f0f0;
    text-align: left;
    font-weight: 500;
`;
const TD = ({ right, bold, children, ...rest }) => (
    <td
        css={css`
            ${tdBase};
            ${right ? 'text-align: right;' : ''} ${bold ? 'font-weight: 800;' : ''}
        `}
        {...rest}
    >
        {children}
    </td>
);

const PipContainer = styled.div`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
    vertical-align: middle;
`;

function StatusPip({ index, audited, skipped }) {
    if (audited) {
        return (
            <PipContainer
                css={css`
                    background: ${greens[0]};
                `}
            >
                <CheckIcon
                    css={css`
                        font-size: 14px !important;
                        color: white;
                    `}
                />
            </PipContainer>
        );
    }
    if (skipped) {
        return (
            <PipContainer
                css={css`
                    background: white;
                    border: 2px dashed ${golds[0]};
                `}
            >
                <SkipNextIcon
                    css={css`
                        font-size: 12px !important;
                        color: ${golds[0]};
                    `}
                />
            </PipContainer>
        );
    }
    return (
        <PipContainer
            css={css`
                background: white;
                border: 1.5px solid ${greys[4]};
                color: ${greys[2]};
            `}
        >
            {index + 1}
        </PipContainer>
    );
}

function MarginChip({ pct }) {
    const health = marginHealth(pct);
    return (
        <span
            css={css`
                font-family: Montserrat, Roboto, Arial, sans-serif;
                font-weight: 700;
                font-size: 12px;
                padding: 3px 9px;
                border-radius: 20px;
                color: ${HEALTH_COLORS[health]};
                background: ${HEALTH_BG[health]};
            `}
        >
            {pct != null ? `${pct.toFixed(1)}%` : '–'}
        </span>
    );
}

export default function RouteOverview({ orders, orderClones, auditStates, onGoToOrder, canEditPerspective }) {
    const cloned = orders.map((o) => (orderClones && orderClones[o.order_id]) || o);

    // Every figure derived from the shipper rate — revenue, margin, and the per-order columns for
    // both — is hidden unless the viewer has shipper-perspective access to all orders in the route.
    const showShipperFigures = canViewShipperAggregate(orders, canEditPerspective);

    const rev = cloned.reduce((a, o) => a + orderShipperTotal(o), 0);
    const cost = cloned.reduce((a, o) => a + orderCarrierTotal(o), 0);
    const margin = routeMarginDollar(cloned);
    const pct = routeMarginPct(cloned);
    const health = showShipperFigures ? marginHealth(pct) : 'neutral';

    return (
        <Root>
            {/* Heading */}
            <Heading>
                <Title>Route Overview</Title>
                <CountChip>{orders.length} orders</CountChip>
            </Heading>

            {/* Route margin headline */}
            <HeadlineCard>
                {showShipperFigures && (
                    <>
                        <StatCell>
                            <StatLabel>Shipper Revenue</StatLabel>
                            <StatValue>{dollarFormatter.format(rev)}</StatValue>
                        </StatCell>
                        <Divider />
                    </>
                )}
                <StatCell>
                    <StatLabel>Carrier Cost</StatLabel>
                    <StatValue>{dollarFormatter.format(cost)}</StatValue>
                </StatCell>
                {showShipperFigures && (
                    <>
                        <Divider />
                        <StatCell style={{ flex: '1.2', background: HEALTH_BG[health] }}>
                            <StatLabel
                                css={css`
                                    color: ${HEALTH_COLORS[health]};
                                `}
                            >
                                Route Margin
                            </StatLabel>
                            <div
                                css={css`
                                    display: flex;
                                    align-items: baseline;
                                    gap: 10px;
                                `}
                            >
                                <StatValue
                                    css={css`
                                        font-size: 32px;
                                        color: ${HEALTH_COLORS[health]};
                                    `}
                                >
                                    {margin > 0 ? '+' : ''}
                                    {dollarFormatter.format(margin)}
                                </StatValue>
                                <span
                                    css={css`
                                        font-family: Montserrat, Roboto, Arial, sans-serif;
                                        font-weight: 800;
                                        font-size: 18px;
                                        color: ${HEALTH_COLORS[health]};
                                    `}
                                >
                                    {pct != null ? `${pct.toFixed(1)}%` : '–'}
                                </span>
                            </div>
                            <StatSub
                                css={css`
                                    color: ${HEALTH_COLORS[health]};
                                `}
                            >
                                {health === 'good'
                                    ? 'Healthy margin'
                                    : health === 'thin'
                                    ? 'Thin margin — review'
                                    : 'Negative margin — review'}
                            </StatSub>
                        </StatCell>
                    </>
                )}
            </HeadlineCard>

            {/* Per-order table */}
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <TH
                                css={css`
                                    width: 40px;
                                `}
                            ></TH>
                            <TH>Order</TH>
                            {showShipperFigures && <TH right>Shipper</TH>}
                            <TH right>Carrier</TH>
                            {showShipperFigures && (
                                <>
                                    <TH right>Margin</TH>
                                    <TH right>%</TH>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((o, i) => {
                            const c = (orderClones && orderClones[o.order_id]) || o;
                            const state = auditStates[o.order_id] || {};
                            const m = orderMarginDollar(c);
                            const mp = orderMarginPct(c);
                            return (
                                <tr
                                    key={o.order_id}
                                    onClick={() => onGoToOrder(i)}
                                    css={css`
                                        cursor: pointer;
                                        &:hover td {
                                            background: #fafafa;
                                        }
                                    `}
                                >
                                    <TD>
                                        <StatusPip index={i} audited={state.audited} skipped={state.skipped} />
                                    </TD>
                                    <TD>
                                        <div
                                            css={css`
                                                font-weight: 700;
                                                color: ${onwardNavy};
                                            `}
                                        >
                                            {o.order_number || o.po_number || o.reference_id}
                                        </div>
                                        <div
                                            css={css`
                                                font-size: 12px;
                                                color: ${greys[1]};
                                                font-weight: 600;
                                            `}
                                        >
                                            {[o.dropoff_city, o.dropoff_state].filter(Boolean).join(', ')}
                                        </div>
                                    </TD>
                                    {showShipperFigures && (
                                        <TD
                                            right
                                            css={css`
                                                font-weight: 600;
                                            `}
                                        >
                                            {dollarFormatter.format(orderShipperTotal(c))}
                                        </TD>
                                    )}
                                    <TD
                                        right
                                        css={css`
                                            font-weight: 600;
                                        `}
                                    >
                                        {dollarFormatter.format(orderCarrierTotal(c))}
                                    </TD>
                                    {showShipperFigures && (
                                        <>
                                            <TD right>
                                                <span
                                                    css={css`
                                                        font-weight: 800;
                                                        color: ${m >= 0 ? greens[0] : reds.cancelled};
                                                    `}
                                                >
                                                    {m > 0 ? '+' : ''}
                                                    {dollarFormatter.format(m)}
                                                </span>
                                            </TD>
                                            <TD right>
                                                <MarginChip pct={mp} />
                                            </TD>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr
                            css={css`
                                border-top: 2px solid ${greys[4]};
                                background: #fafafa;
                            `}
                        >
                            <TD></TD>
                            <TD
                                bold
                                css={css`
                                    color: ${onwardNavy};
                                `}
                            >
                                Route Total
                            </TD>
                            {showShipperFigures && (
                                <TD
                                    right
                                    bold
                                    css={css`
                                        color: ${onwardNavy};
                                    `}
                                >
                                    {dollarFormatter.format(rev)}
                                </TD>
                            )}
                            <TD
                                right
                                bold
                                css={css`
                                    color: ${onwardNavy};
                                `}
                            >
                                {dollarFormatter.format(cost)}
                            </TD>
                            {showShipperFigures && (
                                <>
                                    <TD right>
                                        <span
                                            css={css`
                                                font-weight: 800;
                                                color: ${margin >= 0 ? greens[0] : reds.cancelled};
                                            `}
                                        >
                                            {margin > 0 ? '+' : ''}
                                            {dollarFormatter.format(margin)}
                                        </span>
                                    </TD>
                                    <TD right>
                                        <MarginChip pct={pct} />
                                    </TD>
                                </>
                            )}
                        </tr>
                    </tfoot>
                </Table>
            </TableWrap>

            {/* Progress */}
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-family: Montserrat, Roboto, Arial, sans-serif;
                    font-size: 13px;
                    color: ${greys[1]};
                    font-weight: 600;
                `}
            >
                {orders.filter((o) => auditStates[o.order_id]?.audited).length} of {orders.length} orders audited
            </div>
        </Root>
    );
}
